import { useMemo, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { formatDateToISO } from "../../util/date-formatter";
import { getStatusColor } from "../../util/status-formatter";
import { Order } from "../../types";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { AppButton } from "../../components/ui/AppButton";
import { Icons } from "../../assets/images/icons";
import { DataTable } from "../../components/ui/data-table";
import { useOrderList } from "../../api/order";
import { useTablePrint } from "../../hooks/useTablePrint";
import { useNavigate } from "react-router-dom";
import { Modal } from "antd";
import AdminUpdateOrderModal from "../../components/modals/AdminUdateOrderModal";
import { ExportButton } from "../../components/ui/ExportButton";

function InputPage() {
  const navigate = useNavigate();

  /** State */
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [searchItem, setSearchItem] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedId, setSelectedId] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<
    | "ACCEPTED"
    | "DELIVERED"
    | "INPROGRESS"
    | "PENDING"
    | "REJECTED"
    | "COMPLETED"
    | undefined
  >();

  /** API */
  const { data: orders, isLoading } = useOrderList({
    search: searchItem || undefined,
    page: currentPage - 1,
    limit: pageSize,
  });
  const { handlePrint } = useTablePrint();

  /** Transform orders for table */
  const transformedOrders = useMemo((): Order[] => {
    if (!orders?.content) return [];
    return orders?.content.map((item: Order) => ({
      id: item.id,
      orgName: item.orgName,
      product: item.product || "N/A",
      startingDate: item.startingDate,
      dueDate: item.dueDate,
      orderAmount: item.orderAmount || 0,
      status: item.status,
    }));
  }, [orders]);

  /** Columns */
  const orderColumns = [
    {
      key: "orgName",
      title: "Company Name",
      dataIndex: "orgName",
      align: "left" as const,
    },
    {
      key: "product",
      title: "Product",
      dataIndex: "product",
      align: "left" as const,
    },
    {
      key: "startingDate",
      title: "Start Date",
      dataIndex: "startingDate",
      align: "left" as const,
      render: (v: string) => formatDateToISO(v),
    },
    {
      key: "dueDate",
      title: "Due Date",
      dataIndex: "dueDate",
      align: "left" as const,
      render: (v: string) => formatDateToISO(v),
    },
    {
      key: "orderAmount",
      title: "Quantity in (kg)",
      dataIndex: "orderAmount",
      align: "left" as const,
      render: (v: number) => v?.toLocaleString(),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      align: "left" as const,
      render: (value: string) => (
        <div
          className="px-3 py-1 max-w-[85px] rounded-md text-center text-xs font-medium capitalize"
          style={{
            color: getStatusColor(value, "text"),
            backgroundColor: getStatusColor(value, "bg"),
          }}
        >
          {value}
        </div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      align: "center" as const,
      render: (_: any, record: Order) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedId(record.id);
            setOpen(true);
          }}
          disabled={record.status.toLowerCase() !== "pending"}
          className="h-8 p-0 w-full gap-2"
        >
          Update Status
        </Button>
      ),
    },
  ];

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleRowClick = (order: Order) => navigate(`/orders/${order.id}`);

  const hideModal = () => {
    setOpen(false);
    setSelectedId("");
    setSelectedStatus(undefined);
  };

  const handleSearch = (value: string) => {
    setSearchItem(value);
    setCurrentPage(1);
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between">
        <div className="relative w-full lg:w-64">
          <Input
            value={searchItem}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search orders by name"
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(orderColumns, transformedOrders, "Order Report")
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={orderColumns}
            data={transformedOrders}
            fileName="orders"
            title="Order Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: orders?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
          />
        </div>
      </div>

      <div className="hidden md:block">
        <DataTable<Order>
          data={transformedOrders}
          columns={orderColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: orders?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          onRowClick={handleRowClick}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No orders found"
          rowKey="id"
        />
      </div>

      <Modal
        title={<div style={{ color: "black" }}>Confirmation</div>}
        open={deleteOpen}
        centered
        // onOk={onDelete}
        onCancel={() => setDeleteOpen(false)}
        closable
        style={{ color: "black" }}
      >
        Are you sure you want to delete this Order?
      </Modal>

      <AdminUpdateOrderModal
        open={open}
        selectedId={selectedId}
        hideModal={hideModal}
        orderStatus={selectedStatus}
      />
    </>
  );
}

export default WithAuth(InputPage);
